import { useToast, Text } from '@cad-arc';
import { DrilldownOptionView } from '@mark43/cad-api';
import { createApiErrorHandler, useApiClient, WebDisMetadata } from '@m43/core';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useDexMetadata } from '../use-dex-metadata';
import { useMutation } from '@tanstack/react-query';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { FocusableLink } from '../../common/components/focusable-link';
import { DexQueriessubmitResource } from '@mark43/dex-api';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { FlagRenderer } from '../../queues/components/flag-renderer';
import { ElectronDexMetadata } from '../../electron-bridge/electron-bridge';

export function ResultLineWithDrilldownLink({
    line,
    drilldownOption,
    originatingQueryOriAliasId,
}: Readonly<{
    line: string;
    originatingQueryOriAliasId?: number;
    drilldownOption?: DrilldownOptionView;
}>) {
    const apiClient = useApiClient();
    const toast = useToast();
    const { userProfileView } = useBootstrapState();
    const { dexMetadata } = useDexMetadata();
    const { isFirstResponder } = useApplicationType();

    const submitDrilldownQuery = useMutation(
        ({ drilldownOptionId, oriAliasId }: { drilldownOptionId: number; oriAliasId?: number }) => {
            return apiClient
                .request<DexQueriessubmitResource.SubmitDrilldownSelectionQuery>({
                    service: 'DEX',
                    method: 'POST',
                    url: 'dex_queries/submit/drilldown_selection_inquiry',
                    data: {
                        params: {
                            drilldownOptionId,
                        },
                        dexQuery: 'DRILLDOWN_SELECTION_QUERY',
                        dataExchangeEntryPoint: isFirstResponder
                            ? 'CAD_FIRST_RESPONDER_FORM'
                            : 'CAD_DISPATCH_FORM',
                        deviceName:
                            (dexMetadata as ElectronDexMetadata)?.deviceName ??
                            (dexMetadata as WebDisMetadata)?.machineName,
                        dexStateUserId: userProfileView.dexStateUserId,
                        oriAliasId,
                    },
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: errorOccurredWhile('submitting DEx drilldown query'),
                        });
                    })
                );
        }
    );
    if (!drilldownOption) {
        return (
            <Text fontFamily="mono" variant="bodyMd" as="span" style={{ whiteSpace: 'pre-wrap' }}>
                <FlagRenderer value={line} breakLines allowSpaces />
            </Text>
        );
    }
    const { columnIndex: startIndex, length } = drilldownOption;
    const textBeforeLink = line.substring(0, startIndex);
    const linkText = line.substring(startIndex, startIndex + length);
    const textAfterLink = line.substring(startIndex + length);
    return (
        <Text fontFamily="mono" variant="bodyMd" as="span" style={{ whiteSpace: 'pre-wrap' }}>
            {textBeforeLink}
            <FocusableLink
                isDisabled={submitDrilldownQuery.isLoading}
                onClick={() =>
                    submitDrilldownQuery.mutateAsync({
                        drilldownOptionId: drilldownOption.id,
                        oriAliasId: originatingQueryOriAliasId,
                    })
                }
            >
                {linkText}
            </FocusableLink>
            {textAfterLink}
        </Text>
    );
}
